import React, { useEffect, useState } from "react";
import { renderToString } from "react-dom/server";
import { formatLocationAddress } from "../../obp/utils";
import LocationsList from "./LocationsList";
import Map from "./Map";
import SearchForm from "./SearchForm";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

export const GOOGLE_API_KEY = Shopify.theme.locations_api_key;

export default function Locations({ sectionData }) {
  // Use section data locations if set, or use global obp object
  const srcLocations = !!sectionData.locations
    ? sectionData.locations
    : window.obp.locations;

  const validLocations = [
    ...srcLocations?.filter((location) => {
      return location.latitude && location.longitude;
    }),
  ];

  if (!validLocations || !validLocations.length) return;

  if (!GOOGLE_API_KEY) {
    return (
      <>
        {Shopify.designMode ? (
          <div className="container center h-lg-sans text-color-error">
            Google Maps API key is missing. Please add it in your theme
            settings.
          </div>
        ) : (
          <></>
        )}
      </>
    );
  }

  const circleOptions = {
    fillColor: "#9CC0F9",
    fillOpacity: 0.25,
    strokeColor: "#9CC0F9",
    strokeOpacity: 1,
    strokeWeight: 1,
  };

  const [maps, setMaps] = useState(null);
  const [map, setMap] = useState(null);
  const [markerCluster, setMarkerCluster] = useState(null);
  const [locations, setLocations] = useState(validLocations);
  const [searchResultsLocations, setSearchResultsLocations] = useState(validLocations);
  const [activeLocations, setActiveLocations] = useState(validLocations);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [mapCircle, setMapCircle] = useState(null);
  const [focusedLocation, setFocusedLocation] = useState(null);

  useEffect(() => {
    setActiveLocations(
      currentCategory
        ? searchResultsLocations.filter(
            (location) => location.category === currentCategory
          )
        : searchResultsLocations
    );
  }, [currentCategory, searchResultsLocations]);

  const onSearch = (targetLatLng, radiusMeters) => {
    const newActiveLocations = [];

    locations.forEach((location) => {
      const distance = maps.geometry.spherical.computeDistanceBetween(
        targetLatLng,
        location.mapMarker.position
      );

      if (distance <= radiusMeters) {
        newActiveLocations.push({
          ...location,
          distance: distance,
        });
      }
    });

    // Sort active locations by distance
    newActiveLocations.sort((a, b) => a.distance - b.distance);

    // Remove previous circle
    mapCircle && mapCircle.setMap(null);
    // Create new circle with new params
    setMapCircle(
      new maps.Circle({
        ...circleOptions,
        map,
        center: targetLatLng,
        radius: radiusMeters,
      })
    );

    setSearchResultsLocations(newActiveLocations);
  };

  const populateCategories = (allLocations) => {
    if (allLocations.length) {
      const categoriesArray = allLocations
        .filter((location) => location.category)
        .map((location) => location.category);

      // Return if no categories found
      if (!categoriesArray.length) return;

      // Remove duplicates
      const categories = Array.from(new Set(categoriesArray));
      let defaultCategory = sectionData.defaultCategory;

      // If default category provided - move it to be first, or get the first value to be the default
      if (defaultCategory && categories.includes(defaultCategory)) {
        categories.sort((x, y) => {
          return x == defaultCategory ? -1 : y == defaultCategory ? 1 : 0;
        });
      } else {
        defaultCategory = categories[0];
      }

      setCategories(categories);
      setCurrentCategory(defaultCategory);
    }
  };

  const createMarkers = ({ map, maps }) => {
    const allLocations = [...validLocations];
    const infoWindow = new maps.InfoWindow();

    allLocations.forEach((location) => {
      if (location.latitude && location.longitude) {
        let marker = new maps.Marker({
          position: new maps.LatLng(location.latitude, location.longitude),
          map,
          title: location.name,
          icon: sectionData.mapMarkerURL,
        });

        const infoContent = (
          <div className="location__infobox">
            <h3 className="location__infobox__title h-sm-sans">
              {location.name}
            </h3>
            <div className="location__infobox__details">
              <div className="location__infobox__address">
                {formatLocationAddress(location, false).join(", ")}
              </div>
              {!!location.phone && (
                <div className="location__infobox__phone">
                  <a href={"tel:" + location.phone}>{location.phone}</a>
                </div>
              )}
            </div>
          </div>
        );

        marker.addListener("click", () => {
          setFocusedLocation(location);

          infoWindow.setContent(renderToString(infoContent));
          infoWindow.open({
            anchor: marker,
            map,
          });
        });

        location.mapMarker = marker;
      }
    });

    populateCategories(allLocations);

    setMaps(maps);
    setMap(map);
    setMarkerCluster(new MarkerClusterer({ map }));
    setLocations(allLocations);
    setSearchResultsLocations(allLocations);
  };

  return (
    <div className={"locations".concat(" ", sectionData?.sectionClass)}>
      <div className="container">
        <div className="locations__inner">
          {sectionData?.title && (
            <div className="locations__header center">
              <h2 className="locations__title h-xl h-lg--mobile">
                {sectionData.title}
              </h2>
            </div>
          )}
          <div className="locations__body">
            <SearchForm
              sectionData={sectionData}
              categories={categories}
              currentCategory={currentCategory}
              onCurrentCategory={setCurrentCategory}
              onSearch={onSearch}
              maps={maps}
            />

            <div className="locations__content">
              <LocationsList
                activeLocations={activeLocations}
                sectionData={sectionData}
                onFocusedLocation={setFocusedLocation}
              />
              <Map
                maps={maps}
                map={map}
                markerCluster={markerCluster}
                createMarkers={createMarkers}
                locations={locations}
                activeLocations={activeLocations}
                mapCircle={mapCircle}
                focusedLocation={focusedLocation}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
